import moment from 'moment-timezone';
import { Frequency, Options, RRule, Weekday, WeekdayStr } from 'rrule';

import {
    GableWorkflow,
    GableWorkflowOutcome,
    GableWorkflowRecipient,
    GableWorkflowRun,
    GableWorkflowTrigger,
    GableWorkflowTriggerOption,
    GableWorkflowTriggerParam,
} from './gable-workflows.service';

export type GableRruleFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type GableRruleMonthlyMode = 'day' | 'weekday';

/** Picker state behind one contract rrule (FREQ daily/weekly/monthly, one BYHOUR/BYMINUTE). */
export interface GableRrulePickers {
    frequency: GableRruleFrequency;
    interval: number;
    weekdays: WeekdayStr[];
    monthlyMode: GableRruleMonthlyMode;
    monthDay: number;
    ordinal: number;
    weekday: WeekdayStr;
    month: number;
    yearDay: number;
    time: string;
}

export const GABLE_RRULE_MONTH_DAY_LAST = -1;

export const GABLE_RRULE_FREQUENCIES: { value: GableRruleFrequency; label: string }[] = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
];

export const GABLE_RRULE_MONTHS: { value: number; label: string }[] = [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',
].map((label, i) => ({ value: i + 1, label }));

export const GABLE_RRULE_YEAR_DAYS: { value: number; label: string }[] = Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: ordinal(i + 1) }));

export const GABLE_RRULE_WEEKDAYS: { value: WeekdayStr; label: string }[] = [
    { value: 'MO', label: 'Monday' },
    { value: 'TU', label: 'Tuesday' },
    { value: 'WE', label: 'Wednesday' },
    { value: 'TH', label: 'Thursday' },
    { value: 'FR', label: 'Friday' },
    { value: 'SA', label: 'Saturday' },
    { value: 'SU', label: 'Sunday' },
];

export const GABLE_RRULE_ORDINALS: { value: number; label: string }[] = [
    { value: 1, label: 'First' },
    { value: 2, label: 'Second' },
    { value: 3, label: 'Third' },
    { value: 4, label: 'Fourth' },
    { value: -1, label: 'Last' },
];

export const GABLE_RRULE_MONTH_DAYS: { value: number; label: string }[] = [
    ...Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: ordinal(i + 1) })),
    { value: GABLE_RRULE_MONTH_DAY_LAST, label: 'Last day' },
];

export const DEFAULT_RRULE_PICKERS: GableRrulePickers = {
    frequency: 'weekly',
    interval: 1,
    weekdays: ['MO'],
    monthlyMode: 'day',
    monthDay: 1,
    ordinal: 1,
    weekday: 'MO',
    month: 1,
    yearDay: 1,
    time: '09:00',
};

const FREQ_BY_NAME: Record<GableRruleFrequency, Frequency> = {
    daily: RRule.DAILY,
    weekly: RRule.WEEKLY,
    monthly: RRule.MONTHLY,
    yearly: RRule.YEARLY,
};

const NAME_BY_FREQ: Partial<Record<Frequency, GableRruleFrequency>> = {
    [RRule.DAILY]: 'daily',
    [RRule.WEEKLY]: 'weekly',
    [RRule.MONTHLY]: 'monthly',
    [RRule.YEARLY]: 'yearly',
};

function splitTime(time: string): { hour: number; minute: number } {
    const m = moment(time ?? '', 'HH:mm', true);
    return m.isValid() ? { hour: m.hour(), minute: m.minute() } : { hour: 9, minute: 0 };
}

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

function weekdayStr(w: Weekday | number | string): WeekdayStr {
    if (typeof w === 'string') {
        return w as WeekdayStr;
    }
    const n = typeof w === 'number' ? w : w.weekday;
    return (['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as WeekdayStr[])[n];
}

function first<T>(v: T | T[] | null | undefined): T | undefined {
    return Array.isArray(v) ? v[0] : (v ?? undefined);
}

/** Contract rrule body (no `RRULE:` prefix, no DTSTART) from the pickers. */
export function buildRrule(p: GableRrulePickers): string {
    const { hour, minute } = splitTime(p.time);
    const options: Partial<Options> = { freq: FREQ_BY_NAME[p.frequency] };
    if (p.interval > 1) {
        options.interval = p.interval;
    }
    if (p.frequency === 'weekly' && p.weekdays.length > 0) {
        options.byweekday = p.weekdays.map((d) => Weekday.fromStr(d)).sort((a, b) => a.weekday - b.weekday);
    }
    if (p.frequency === 'monthly') {
        if (p.monthlyMode === 'weekday') {
            options.byweekday = [Weekday.fromStr(p.weekday)];
            options.bysetpos = p.ordinal;
        } else {
            options.bymonthday = p.monthDay;
        }
    }
    if (p.frequency === 'yearly') {
        options.bymonth = p.month;
        options.bymonthday = p.yearDay;
    }
    options.byhour = hour;
    options.byminute = minute;
    return new RRule(options).toString().replace(/^RRULE:/, '');
}

/** Pickers from a contract rrule, or null when the rule is outside what the pickers can show. */
export function parseRrule(rrule: string | null | undefined): GableRrulePickers | null {
    if (!rrule) {
        return null;
    }
    let rule: RRule;
    try {
        rule = RRule.fromString(rrule.replace(/^RRULE:/, ''));
    } catch {
        return null;
    }
    const o = rule.origOptions;
    const frequency = o.freq === undefined ? undefined : NAME_BY_FREQ[o.freq];
    if (!frequency) {
        return null;
    }
    const hour = first(o.byhour) ?? 9;
    const minute = first(o.byminute) ?? 0;
    const p: GableRrulePickers = { ...DEFAULT_RRULE_PICKERS, frequency, interval: o.interval ?? 1, time: `${pad(hour)}:${pad(minute)}` };
    const byweekday = o.byweekday === null || o.byweekday === undefined ? [] : (Array.isArray(o.byweekday) ? o.byweekday : [o.byweekday]);
    if (frequency === 'weekly') {
        p.weekdays = byweekday.map(weekdayStr);
    }
    if (frequency === 'monthly') {
        const monthDay = first(o.bymonthday);
        if (monthDay !== undefined) {
            p.monthlyMode = 'day';
            p.monthDay = monthDay;
        } else if (byweekday.length > 0) {
            p.monthlyMode = 'weekday';
            p.weekday = weekdayStr(byweekday[0]);
            p.ordinal = first(o.bysetpos) ?? 1;
        }
    }
    if (frequency === 'yearly') {
        p.month = first(o.bymonth) ?? 1;
        p.yearDay = first(o.bymonthday) ?? 1;
    }
    return p;
}

function joinAll(items: string[]): string {
    if (items.length <= 1) {
        return items[0] ?? '';
    }
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function every(interval: number, unit: string): string {
    return interval === 2 ? `Every other ${unit}` : `Every ${interval} ${unit}s`;
}

/** Live preview in core's wording, e.g. "Weekly on Monday and Thursday at 8:00 AM", "Monthly on the last day of the month at 5:30 PM". */
export function rruleText(rrule: string | null | undefined): string {
    const p = parseRrule(rrule);
    if (!p) {
        return '';
    }
    const time = formatRunTime(p.time);
    if (p.frequency === 'daily') {
        return `${p.interval > 1 ? every(p.interval, 'day') : 'Daily'} at ${time}`;
    }
    if (p.frequency === 'weekly') {
        if (p.weekdays.length === 0) {
            return 'Pick at least one day';
        }
        const order = GABLE_RRULE_WEEKDAYS.map((d) => d.value);
        const days = [...p.weekdays]
            .sort((a, b) => order.indexOf(a) - order.indexOf(b))
            .map((d) => GABLE_RRULE_WEEKDAYS.find((w) => w.value === d)?.label ?? d);
        return `${p.interval > 1 ? every(p.interval, 'week') : 'Weekly'} on ${joinAll(days)} at ${time}`;
    }
    if (p.frequency === 'monthly') {
        const lead = p.interval > 1 ? every(p.interval, 'month') : 'Monthly';
        if (p.monthlyMode === 'weekday') {
            const nth = (GABLE_RRULE_ORDINALS.find((o) => o.value === p.ordinal)?.label ?? 'First').toLowerCase();
            const day = GABLE_RRULE_WEEKDAYS.find((w) => w.value === p.weekday)?.label ?? p.weekday;
            return `${lead} on the ${nth} ${day} of the month at ${time}`;
        }
        const dayOfMonth = p.monthDay === GABLE_RRULE_MONTH_DAY_LAST ? 'last day' : ordinal(p.monthDay);
        return `${lead} on the ${dayOfMonth} of the month at ${time}`;
    }
    const month = GABLE_RRULE_MONTHS.find((m) => m.value === p.month)?.label ?? 'January';
    return `${p.interval > 1 ? every(p.interval, 'year') : 'Yearly'} on ${month} ${ordinal(p.yearDay)} at ${time}`;
}

/** "HH:MM" (24h) to "9:00 AM". */
export function formatRunTime(runTime: string | null | undefined): string {
    const m = moment(runTime ?? '', 'HH:mm', true);
    return m.isValid() ? m.format('h:mm A') : (runTime ?? '');
}

export function ordinal(day: number): string {
    const rem100 = day % 100;
    if (rem100 >= 11 && rem100 <= 13) {
        return `${day}th`;
    }
    const suffixes: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };
    return `${day}${suffixes[day % 10] ?? 'th'}`;
}

/** ISO instant on the org's wall clock (browser local when no timezone), e.g. "Mon, Sep 21 at 9:00 AM". */
export function formatInTimezone(iso: string | null | undefined, timezone: string | null | undefined): string {
    if (!iso) {
        return '';
    }
    const m = moment(iso);
    if (!m.isValid()) {
        return '';
    }
    return (timezone ? m.tz(timezone) : m).format('ddd, MMM D [at] h:mm A');
}

export function relativeTime(iso: string | null | undefined): string {
    if (!iso) {
        return '';
    }
    const m = moment(iso);
    return m.isValid() ? m.fromNow() : '';
}

/** "Emailed 2 people, 3 suggestions staged, filed to Documents", "Nothing to report" or "Failed: …" for a finished run. */
export function runOutcomeText(run: Pick<GableWorkflowRun, 'status' | 'error' | 'candidatesStaged' | 'emailsSent' | 'documentsSaved'>): string {
    if (run.status === 'failed') {
        const reason = (run.error ?? '').trim();
        return reason ? `Failed: ${reason}` : 'Failed';
    }
    const parts: string[] = [];
    const emailed = run.emailsSent ?? 0;
    const staged = run.candidatesStaged ?? 0;
    if (emailed > 0) {
        parts.push(`Emailed ${emailed} ${emailed === 1 ? 'person' : 'people'}`);
    }
    if (staged > 0) {
        parts.push(`${staged} ${staged === 1 ? 'suggestion' : 'suggestions'} staged`);
    }
    if ((run.documentsSaved ?? 0) > 0) {
        parts.push(parts.length > 0 ? 'filed to Documents' : 'Filed to Documents');
    }
    return parts.length > 0 ? parts.join(', ') : 'Nothing to report';
}

/** A queued run the box has already turned away at least once (SD batch H 1.2): it waits for capacity rather than failing. */
export function isWaitingOnCapacity(run: Pick<GableWorkflowRun, 'status' | 'dispatchAttempts'> | null | undefined): boolean {
    return run?.status === 'queued' && (run.dispatchAttempts ?? 0) > 0;
}

export function inFlightText(run: Pick<GableWorkflowRun, 'status' | 'dispatchAttempts'>): string {
    if (run.status === 'running') {
        return 'Running now';
    }
    return isWaitingOnCapacity(run) ? 'Waiting, Gable is busy' : 'Queued';
}

/** List card line 4: the outcome text plus when, or the in-flight state. */
export function runSummary(run: GableWorkflowRun | null | undefined): string {
    if (!run) {
        return 'Has not run yet';
    }
    if (run.status === 'queued' || run.status === 'running') {
        return inFlightText(run);
    }
    const when = relativeTime(run.finishedAt ?? run.startedAt ?? run.createdAt);
    const text = runOutcomeText(run);
    return when ? `${text} · ${when}` : text;
}

/** Finish toast detail: "Emailed N people. M suggestions in Activity." or "Nothing to report this time." */
export function runFinishDetail(run: Pick<GableWorkflowRun, 'candidatesStaged' | 'emailsSent'>): string {
    const parts: string[] = [];
    const emailed = run.emailsSent ?? 0;
    const staged = run.candidatesStaged ?? 0;
    if (emailed > 0) {
        parts.push(`Emailed ${emailed} ${emailed === 1 ? 'person' : 'people'}.`);
    }
    if (staged > 0) {
        parts.push(`${staged} ${staged === 1 ? 'suggestion' : 'suggestions'} in Activity.`);
    }
    return parts.length > 0 ? parts.join(' ') : 'Nothing to report this time.';
}

export function latestRun(w: Pick<GableWorkflow, 'runs'>): GableWorkflowRun | null {
    return (w.runs ?? [])[0] ?? null;
}

export function hasOutcome(outcomes: GableWorkflowOutcome[] | null | undefined, type: GableWorkflowOutcome['type']): boolean {
    return (outcomes ?? []).some((o) => o.type === type);
}

export function emailRecipients(outcomes: GableWorkflowOutcome[] | null | undefined): GableWorkflowRecipient[] {
    const email = (outcomes ?? []).find((o): o is Extract<GableWorkflowOutcome, { type: 'email' }> => o.type === 'email');
    return email?.recipients ?? [];
}

/** "Catherine D", "Catherine D and Jim K", "Catherine D, Jim K and Ann B", "Catherine D, Jim K and 3 others". */
export function joinNames(names: string[]): string {
    const clean = names.map((n) => n.trim()).filter(Boolean);
    if (clean.length <= 1) {
        return clean[0] ?? '';
    }
    if (clean.length <= 3) {
        return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
    }
    const rest = clean.length - 2;
    return `${clean[0]}, ${clean[1]} and ${rest} others`;
}

export function documentOutcome(outcomes: GableWorkflowOutcome[] | null | undefined): Extract<GableWorkflowOutcome, { type: 'document' }> | null {
    return (outcomes ?? []).find((o): o is Extract<GableWorkflowOutcome, { type: 'document' }> => o.type === 'document') ?? null;
}

/** Core's "Documents / Board Materials / Minutes" as is; the picker's names joined the same way; blank or root reads "Documents". */
export function documentPathText(path: string | string[] | null | undefined): string {
    const names = (Array.isArray(path) ? path : (path ?? '').split('/')).map((n) => n.trim()).filter(Boolean);
    if (names[0] !== 'Documents') {
        names.unshift('Documents');
    }
    return names.join(' / ');
}

/** Outcomes in words: "Emails Catherine D and Jim K. Stages suggestions in Activity. Saves the summary to Documents / Board Materials." */
export function outcomesText(outcomes: GableWorkflowOutcome[] | null | undefined): string {
    const sentences: string[] = [];
    const recipients = emailRecipients(outcomes);
    if (hasOutcome(outcomes, 'email')) {
        const names = joinNames(recipients.map((r) => r.name));
        sentences.push(names ? `Emails ${names}.` : 'Emails a summary.');
    }
    if (hasOutcome(outcomes, 'suggestions')) {
        sentences.push('Stages suggestions in Activity.');
    }
    const document = documentOutcome(outcomes);
    if (document) {
        sentences.push(`Saves the summary to ${documentPathText(document.folderPath)}.`);
    }
    return sentences.join(' ');
}

/** Client-side first occurrence on the org's wall clock, e.g. "Mon, Sep 21 at 9:00 AM"; blank when the rule cannot be read. */
export function nextOccurrence(rrule: string | null | undefined, timezone: string | null | undefined, from: moment.Moment = moment()): string {
    const p = parseRrule(rrule);
    if (!rrule || (p?.frequency === 'weekly' && p.weekdays.length === 0)) {
        return '';
    }
    let rule: RRule;
    try {
        rule = RRule.fromString(rrule.replace(/^RRULE:/, ''));
    } catch {
        return '';
    }
    const local = timezone ? from.clone().tz(timezone) : from.clone();
    const floating = new Date(Date.UTC(local.year(), local.month(), local.date(), local.hour(), local.minute(), local.second()));
    const start = new Date(Date.UTC(local.year() - 1, local.month(), local.date()));
    const next = new RRule({ ...rule.origOptions, dtstart: start }).after(floating, false);
    return next ? moment.utc(next).format('ddd, MMM D [at] h:mm A') : '';
}

export function savedRrule(trigger: GableWorkflowTrigger | null | undefined): string | null {
    return trigger?.type === 'schedule' ? trigger.rrule : null;
}

export function triggerFilterLabels(option: Pick<GableWorkflowTriggerOption, 'filter'> | null | undefined, ids: number[]): string[] {
    const options = option?.filter?.options ?? [];
    return ids.map((id) => options.find((o) => o.value === id)?.label).filter((l): l is string => !!l);
}

export function triggerDaysParam(option: Pick<GableWorkflowTriggerOption, 'params'> | null | undefined): GableWorkflowTriggerParam | null {
    return (option?.params ?? []).find((p) => p.key === 'days') ?? null;
}

/** The catalog label with its literal "N" replaced: "An invoice is N days past due" with 30 reads "An invoice is 30 days past due". */
export function triggerLabelWithDays(option: Pick<GableWorkflowTriggerOption, 'label' | 'labelTemplate' | 'params'> | null | undefined, days?: number | null): string {
    if (!option) {
        return '';
    }
    const param = triggerDaysParam(option);
    if (!param) {
        return option.label;
    }
    const value = typeof days === 'number' && Number.isFinite(days) ? days : param.default;
    return (option.labelTemplate ?? option.label).replace(/\{days\}/g, String(value)).replace(/\bN\b/, String(value));
}

/** Core's cadence label for an event trigger: "A violation has sat in a status for 5 days (Hearing scheduled)". */
export function eventTriggerLabel(option: Pick<GableWorkflowTriggerOption, 'label' | 'labelTemplate' | 'filter' | 'params'> | null | undefined, ids: number[], days?: number | null): string {
    if (!option) {
        return '';
    }
    const labels = triggerFilterLabels(option, ids);
    const label = triggerLabelWithDays(option, days);
    return labels.length > 0 ? `${label} (${labels.join(', ')})` : label;
}

/** When-step preview: "Runs when a homeowner submits a request (Architectural Review Request)" or "Runs once a day, for every record where an invoice is 30 days past due". */
export function eventPreviewText(option: Pick<GableWorkflowTriggerOption, 'label' | 'labelTemplate' | 'filter' | 'params' | 'kind'> | null | undefined, ids: number[], days?: number | null): string {
    const label = eventTriggerLabel(option, ids, days);
    if (!label) {
        return '';
    }
    if (option?.kind === 'daily') {
        return `Runs once a day, for every record where ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
    }
    return `Runs when ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

export function formatDuration(ms: number | null | undefined): string {
    if (ms === null || ms === undefined) {
        return '';
    }
    if (ms < 1000) {
        return `${ms} ms`;
    }
    const seconds = ms / 1000;
    if (seconds < 90) {
        return `${seconds.toFixed(seconds < 10 ? 1 : 0)} s`;
    }
    return `${Math.round(seconds / 60)} min`;
}
